import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSubjectMember } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { addHours, isPast } from 'date-fns/esm'
import DOMPurify from 'dompurify'
import { isEmpty, isNil } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import styled, { css, useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { Flex, FlexColumn, Margin } from '@/@bothrs-ui/web/components/layout'
import { MediaQuery } from '@/@bothrs-ui/web/components/media-query/MediaQuery'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { CancelAppointmentModal } from '@/modules/shared/components/cancel-appointment-modal/CancelAppointmentModal'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { capitalize, formatInTZ, formatLocale } from '@/modules/shared/helpers/locale'
import { navigateMaps } from '@/modules/shared/helpers/navigation'
import { useToast } from '@/modules/shared/hooks/useToast'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'
import { copyToClipboard } from '@/utils/clipboard'
import { formatToWaitTime } from '@/utils/time'
import { RelatedItems } from '../../../shared/components/related-items/RelatedItems'
import { AppointmentDetail } from '../../components/appointment-detail/AppointmentDetail'
import { AppointmentNumberCard } from '../../components/appointment-number/AppointmentNumber'
import { ContactInfoCard } from '../../components/contact-info-card/ContactInfoCard'
import { DoctorInfo } from '../../components/doctor-info/DoctorInfo'
import { LocationCard } from '../../components/location-card/LocationCard'

import type { TimelineContextType } from '@/modules/timeline/types/timeline-context.type'
import type { Appointment } from '../../types/appointment.types'
import type { Location } from '../../types/location.types'

const CANCEL_DEADLINE_HOURS = 72

const AppointmentDetailPage = () => {
  const navigate = useNavigate()
  const { appointments, forceReload, isLoading } = useOutletContext<TimelineContextType>()

  const theme = useTheme()
  const subjectMember = useSubjectMember()
  const { showSuccessToast } = useToast()
  const { id } = useParams()

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false)
  const [isCancellingAppointment, setIsCancellingAppointment] = useState<boolean>(false)

  // Hide navigation on native devices
  useHideNativeNavigation()

  // Template variables
  // ------------------------------------------------------------------------- /
  const appointment: Appointment | undefined = useMemo(() => {
    setIsCancellingAppointment(false)

    if (appointments.value && id) {
      return appointments.value.find((appointment: Appointment) => appointment.id.toString() === id)
    }
  }, [appointments.value, id])

  const location = useMemo(() => {
    if (appointment?.meta?.location && typeof appointment?.meta?.location !== 'string') {
      return appointment.meta.location
    } else if (appointment?.meta?.esign?.location) {
      const location = appointment?.meta?.esign?.location
      const address = location?.address
      return {
        name: location?.name?.nl,
        address: {
          street: address.street.nl,
          city: address.city.nl,
          country: address.country.nl,
          postcode: address.postcode.nl,
        },
      } as Location
    }
  }, [appointment])

  const careplan = appointment?.activities?.at(0)?.careplan?.display

  const appointmentNumber = appointment?.meta?.appointmentNumber

  const appointmentTitle = i18n._(activityTypeTranslations.Appointment as string)

  const dateString = appointment?.start
    ? capitalize(formatLocale(new Date(appointment.start), 'EEEE dd/MM/yyyy'))
    : undefined
  const startTime = appointment?.start ? formatInTZ(new Date(appointment.start), 'HH:mm') : undefined
  const waitTime = appointment?.meta?.synops?.waitTime

  // Address rendered as separate lines: street on one, postcode + city on the next.
  const addressLines = useMemo(() => {
    if (!location) {
      return []
    }
    if (typeof location.address === 'string') {
      return [location.address]
    }
    if (location.address) {
      return [
        location.address.street,
        [location.address.postcode, location.address.city].filter(Boolean).join(' '),
      ].filter((line): line is string => Boolean(line))
    }
    return []
  }, [location])

  const hasAddress = addressLines.length > 0

  // Location names follow a "City - Place" convention; the city is emphasised.
  const [locationCity, locationPlace] = (location?.name ?? '').split(' - ')

  const phone =
    appointment?.meta?.location?.phone ||
    appointment?.meta?.esign?.contactInfo?.phone ||
    t({
      id: 'globals.azmm-contact.phone',
      comment: 'General contact info phone number of AZMM',
      message: '+32 9 246 46 46',
    })

  // Composed once: the card is the same content whether or not it can be navigated to.
  const locationContent = location ? (
    <FlexColumn gap={theme.UI.Spacing.Space1}>
      {location.name ? (
        <LocationCard.Name>
          {locationPlace ? (
            <>
              <LocationCard.City>{locationCity}</LocationCard.City> - {locationPlace}
            </>
          ) : (
            location.name
          )}
        </LocationCard.Name>
      ) : null}
      {addressLines.map((line) => (
        <LocationCard.Address key={line}>{line}</LocationCard.Address>
      ))}
    </FlexColumn>
  ) : null

  const canCancel = useMemo(() => {
    if (appointment) {
      if (appointment.meta.cancellable === false) {
        return false
      }

      return (
        appointment &&
        appointment.status !== 'cancelled' &&
        !isCancellingAppointment &&
        !isPast(addHours(new Date(appointment.start), -CANCEL_DEADLINE_HOURS))
      )
    }
    return true
  }, [appointment, isCancellingAppointment])

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  function handleCancelIntentAppointment() {
    setShowCancelModal(true)
  }

  function handleCloseModal() {
    forceReload()
    setShowCancelModal(false)
  }

  function handleCloseAfterCancel() {
    forceReload()
    setShowCancelModal(false)
    navigate(-1)
  }

  function handleIsCancelling() {
    if (appointment) setIsCancellingAppointment(true)
  }

  function handleBookAppointment() {
    navigate('/appointment')
  }

  function handleCopyAppointmentNumber() {
    if (!appointmentNumber) {
      return
    }

    mixpanel.track('appointment_number_copy')
    copyToClipboard(appointmentNumber).then((copied) => {
      if (copied) {
        showSuccessToast({
          message: t({
            id: 'appointments.detail.appointment-number.copied',
            message: 'Afspraaknummer gekopieerd',
          }),
        })
      }
    })
  }

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (!appointment) {
      if (!appointments.value && !isLoading) {
        forceReload()
      } else if (appointments.value) {
        navigate('/timeline')
      }
    }
  }, [appointments, appointment, forceReload, isLoading, navigate])

  // Render Methods
  // ------------------------------------------------------------------------- /
  return (
    <>
      <StContentPageLayout
        backgroundColor={theme.Colors.Background.White}
        noPadding
        noScroll
        header={
          <StHeaderWrapper>
            <AppHeader
              backgroundColor={theme.Colors.Background.White}
              primaryAction={{
                label: i18n._(globalDefinitions.goBack),
                icon: ['far', 'chevron-left'],
                onClick: () => {
                  navigate(-1)
                },
              }}
            />
            <Flex justifyContent='space-between' alignItems='center'>
              <H3 noMargin>{appointmentTitle}</H3>
              {careplan ? <Chip type='Primary'>{careplan}</Chip> : null}
            </Flex>
          </StHeaderWrapper>
        }
      >
        <StContainer>
          {appointments.isLoading && isNil(appointments.value) ? (
            <AnimatedLoader />
          ) : (
            <>
              <StContent>
                {/* Title - visible on desktop only (on mobile it's in the header) */}
                <MediaQuery query={breakpoints.fromM}>
                  <Flex justifyContent='space-between' alignItems='center'>
                    <H3 noMargin>{appointmentTitle}</H3>
                    {careplan ? <Chip type='Primary'>{careplan}</Chip> : null}
                  </Flex>
                </MediaQuery>

                {/* Doctor */}
                {appointment?.meta?.esign?.doctor ? (
                  <DoctorInfo
                    doctor={appointment.meta.esign.doctor}
                    description={appointment?.meta.esign.specialisation?.title.nl}
                    size='large'
                  />
                ) : null}

                {/* Day - Time */}
                {appointment?.start ? (
                  <FlexColumn gap={theme.UI.Spacing.Space1}>
                    <Flex alignItems='center' gap={theme.UI.Spacing.Space1}>
                      <AppointmentDetail.Icon icon={['fal', 'calendar']} />
                      <AppointmentDetail.Date>{dateString}</AppointmentDetail.Date>
                    </Flex>
                    {appointment.meta?.hiddenInApp ? null : (
                      <Flex alignItems='center' gap={theme.UI.Spacing.Space2}>
                        <AppointmentDetail.Icon icon={['fal', 'clock']} />
                        <AppointmentDetail.Time>{startTime}</AppointmentDetail.Time>
                        {waitTime ? (
                          <AppointmentDetail.WaitTime>{formatToWaitTime(waitTime)}</AppointmentDetail.WaitTime>
                        ) : null}
                      </Flex>
                    )}
                  </FlexColumn>
                ) : null}

                {/* Location */}
                {location && hasAddress ? (
                  <LocationCard.Navigate
                    onClick={() => navigateMaps(location.address)}
                    // The label interpolates the location so the accessible name carries both the
                    // purpose and the address, rather than replacing one with the other.
                    aria-label={t({
                      id: 'appointments.detail.location.navigate',
                      comment: 'Accessible label of the appointment location card',
                      message: 'Navigeer naar {location}',
                      values: { location: [location.name, ...addressLines].filter(Boolean).join(', ') },
                    })}
                  >
                    {locationContent}
                    <LocationCard.Icon icon={['fal', 'location-arrow']} />
                  </LocationCard.Navigate>
                ) : null}

                {/* Without an address there is nothing to navigate to, so the card stays plain content */}
                {location && !hasAddress ? <LocationCard>{locationContent}</LocationCard> : null}

                {/* Appointment number */}
                {appointmentNumber ? (
                  <AppointmentNumberCard onCopy={handleCopyAppointmentNumber}>
                    <AppointmentNumberCard.Label>
                      {t({
                        id: 'appointments.detail.appointment-number.label',
                        message: 'Afspraaknummer',
                      })}
                    </AppointmentNumberCard.Label>
                    <Flex alignItems='center' gap={theme.UI.Spacing.Space2}>
                      <AppointmentNumberCard.Value>{appointmentNumber}</AppointmentNumberCard.Value>
                      <AppointmentNumberCard.Icon />
                    </Flex>
                    <AppointmentNumberCard.Hint>
                      {t({
                        id: 'appointments.detail.appointment-number.hint',
                        message: 'Houd dit nummer bij de hand aan de aanmeldkiosk.',
                      })}
                    </AppointmentNumberCard.Hint>
                  </AppointmentNumberCard>
                ) : null}

                {/* Notes */}
                {appointment?.meta?.noShow ? (
                  <StNoShows>
                    <Flex alignItems='center' gap={theme.UI.Spacing.Space2}>
                      <FontAwesomeIcon icon={['far', 'triangle-exclamation']} color={theme.Colors.Status.Error} />
                      <Span type='BodyLargeBold' color={theme.Colors.Status.Error}>
                        {t({
                          id: 'appointments.detail.noshow.title',
                          message: 'Je was niet op je afspraak',
                        })}
                      </Span>
                    </Flex>
                    <Span type='BodyMediumRegular'>
                      {t({
                        id: 'appointments.detail.noshow.description.header',
                        message: '{name},',
                        values: { name: subjectMember?.givenName },
                      })}
                    </Span>
                    <Span type='BodyMediumRegular'>
                      {t({
                        id: 'appointments.detail.noshow.description.body1',
                        message:
                          'Was je verhinderd? Contacteer dan steeds het secretariaat van je zorgverlener. Zo kunnen we andere patiënten helpen en onze dienstverlening efficiënt organiseren.',
                      })}
                    </Span>
                    <Span type='BodyMediumRegular'>
                      {t({
                        id: 'appointments.detail.noshow.description.body2',
                        message:
                          'Doe je dat minder dan 24 uur voor je geplande afspraak of niet? Dan zal je een forfaitaire kost van 25 euro aangerekend worden. Eventuele uitzonderingen op deze regel staan vermeld op de afsprakenpagina van de dienst op onze website.',
                      })}
                    </Span>
                    <Span type='BodyMediumRegular'>
                      {t({
                        id: 'appointments.detail.noshow.description.body3',
                        message: 'Bedankt voor je begrip.',
                      })}
                    </Span>
                  </StNoShows>
                ) : appointment?.meta?.notes && !isEmpty(appointment.meta.notes) ? (
                  <FlexColumn gap={theme.UI.Spacing.Space3}>
                    <Span type='BodyMediumBold'>
                      {t({
                        id: 'appointments.detail.about',
                        message: 'Over deze afspraak',
                      })}
                    </Span>
                    {appointment?.meta?.notes?.map((note, index) => (
                      <StNotes key={index} type='BodyMediumRegular'>
                        <div
                          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(note),
                          }}
                        />
                      </StNotes>
                    ))}
                  </FlexColumn>
                ) : null}

                {/* Contact */}
                <>
                  {canCancel ? null : (
                    <FlexColumn gap={theme.UI.Spacing.Space2}>
                      <Span type='BodyMediumBold'>
                        {t({
                          id: 'appointments.detail.cancel-or-change',
                          comment: 'Cancel or change contact message',
                          message: 'Afspraak annuleren of wijzigen?',
                        })}
                      </Span>
                      <Span type='BodyMediumRegular'>
                        {t({
                          id: 'appointments.detail.contact',
                          comment: 'Contact message for appointment detail',
                          message: 'Neem telefonisch contact op met het secretariaat.',
                        })}
                      </Span>
                    </FlexColumn>
                  )}
                  <ContactInfoCard>
                    <ContactInfoCard.Name>
                      {location?.name ||
                        appointment?.meta?.esign?.contactInfo?.name ||
                        t({
                          id: 'globals.azmm-contact.name',
                          comment: 'General contact info name of AZMM',
                          message: 'AZ Maria Middelares',
                        })}
                    </ContactInfoCard.Name>
                    <ContactInfoCard.Phone
                      href={`tel:${phone}`}
                      onClick={() => mixpanel.track('appointment_contact', { type: 'phone', origin: 'detail' })}
                    >
                      <ContactInfoCard.PhoneNumber>{phone}</ContactInfoCard.PhoneNumber>
                      <ContactInfoCard.Icon icon={['fal', 'phone']} />
                    </ContactInfoCard.Phone>
                  </ContactInfoCard>
                </>

                {/* Related triggers */}
                {appointment?.trigger.trim() && <RelatedItems trigger={appointment?.trigger} />}

                <Margin my={theme.UI.Spacing.Space6} />
              </StContent>
              <StButtonContainer>
                {canCancel ? null : (
                  <>
                    <Span type='BodySmallRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
                      {appointment && appointment.meta.cancellable === false
                        ? t({
                            id: 'appointments.detail.disable-cancellable',
                            comment: 'Disable description for a non-cancellable appointment',
                            message:
                              'Je kan deze afspraak enkel telefonisch annuleren. Neem contact op met het secretariaat.',
                          })
                        : appointment && isPast(new Date(appointment.start))
                          ? t({
                              id: 'appointments.detail.disable-past',
                              comment: 'Disable description for cancel appointment because it is in the past',
                              message:
                                'Je kan deze afspraak niet langer annuleren omdat deze in het verleden plaatsvond.',
                            })
                          : appointment?.status === 'cancelled' || isCancellingAppointment
                            ? t({
                                id: 'appointments.detail.disable-cancelled',
                                comment: 'Cancelled description',
                                message: 'We zijn je afspraak aan het annuleren. Dit kan enkele minuten duren.',
                              })
                            : t({
                                id: 'appointments.detail.disable-deadline',
                                comment:
                                  'Disable description for cancel appointment because it is too close to the deadline',
                                message:
                                  'Je kan deze afspraak enkel telefonisch annuleren omdat deze plaatsvindt binnen {hours} uur.',
                                values: {
                                  hours: CANCEL_DEADLINE_HOURS,
                                },
                              })}
                    </Span>
                    <Margin mb={theme.UI.Spacing.Space2} />
                  </>
                )}
                <Button variant='Secondary' onClick={handleCancelIntentAppointment} disabled={!canCancel}>
                  {t({
                    id: 'appointments.detail.tertiary',
                    comment: 'Tertiary button text on the appointment detail page',
                    message: 'Afspraak annuleren',
                  })}
                </Button>
              </StButtonContainer>
            </>
          )}
        </StContainer>
      </StContentPageLayout>

      <CancelAppointmentModal
        key={appointment?.id}
        appointment={appointment}
        isVisible={showCancelModal}
        onAction={handleBookAppointment}
        onClose={handleCloseModal}
        onCloseAfterCancel={handleCloseAfterCancel}
        onCancelling={handleIsCancelling}
      />
    </>
  )
}

// Centres a full-width section on its 600px content column from the M breakpoint up. Shared by the
// header, the scrollable content, and the button bar so the constraint lives in one place.
const contentColumnCSS = css`
  @media ${breakpoints.fromM} {
    padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
      ${({ theme }) => theme.UI.SpacingPx.Space6} !important;
    margin: 0 auto;
    max-width: 600px;
    width: 100%;
  }
`

const StContentPageLayout = styled(ContentPageLayout)`
  ${ContentPagePaddingCSS}
  padding-left: 0;
  padding-right: 0;
`

const StContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  grid-template-areas:
    'content'
    'button';
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

const StButtonContainer = styled.div`
  grid-area: button;
  box-shadow: 0px -1px 20px rgba(4, 24, 49, 0.07);
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
    ${({ theme }) => theme.UI.SpacingPx.Space6};
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }

  ${contentColumnCSS}

  @media ${breakpoints.fromM} {
    overflow-x: hidden;
  }

  @media ${breakpoints.s} {
    box-shadow: none;
  }
`

const StNoShows = styled(FlexColumn)`
  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space5};
    padding: ${theme.UI.SpacingPx.Space6} ${theme.UI.SpacingPx.Space5};
    border-radius: ${theme.UI.BorderRadius.Small}px;
    background: #FFF4F3;
  `}
`

const StNotes = styled(Span)`
  word-break: break-word;
  -webkit-touch-callout: text;
  -webkit-user-select: text;
  -khtml-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
  ul {
    list-style-type: disc;
  }

  li {
    margin-left: ${({ theme }) => theme.UI.SpacingPx.Space3};
  }

  a {
    color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  }
`

const StHeaderWrapper = styled.div`
  ${ContentPagePaddingCSS}
  /* Separates the title from the first content item on mobile, matching the in-content title's gap
   on larger screens (where the header title is hidden). */
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.Colors.Background.White};

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space4};
  `}

  ${contentColumnCSS}

  @media ${breakpoints.fromM} {
    padding-bottom: 0 !important;
  }
`

// Positions the appointment detail in the page grid and owns its scroll; the component itself only
// stacks its content.
const StContent = styled(AppointmentDetail)`
  grid-area: content;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${ContentPagePaddingCSS}
  padding-top: 0;
  padding-bottom: 0;
  ${contentColumnCSS}
`

export { AppointmentDetailPage }
